export type DateBucketType =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'thisYear'
  | 'older';

/**
 * Get ISO week number for a given date.
 * ISO 8601: weeks start on Monday, week 1 contains first Thursday of the year.
 */
function getISOWeekNumber(date: Date): number {
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  // Set to nearest Thursday: current date + 4 - current day number (Monday=1, Sunday=7)
  const dayNumber = target.getDay() || 7;
  target.setDate(target.getDate() + 4 - dayNumber);
  const yearStart = new Date(target.getFullYear(), 0, 1);
  return Math.ceil(
    ((target.getTime() - yearStart.getTime()) / 86400000 + 1) / 7
  );
}

/**
 * Get the ISO week year for a given date.
 * This may differ from the calendar year at year boundaries.
 */
function getISOWeekYear(date: Date): number {
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayNumber = target.getDay() || 7;
  target.setDate(target.getDate() + 4 - dayNumber);
  return target.getFullYear();
}

/**
 * Determine the date bucket type for a given date relative to "now".
 * Compares calendar days (not 24h windows) using local date parts for timezone safety.
 */
export function getDateBucketType(date: Date, now?: Date): DateBucketType {
  const reference = now ?? new Date();

  const dateYear = date.getFullYear();
  const dateMonth = date.getMonth();
  const dateDay = date.getDate();

  const refYear = reference.getFullYear();
  const refMonth = reference.getMonth();
  const refDay = reference.getDate();

  // Today: same calendar day
  if (dateYear === refYear && dateMonth === refMonth && dateDay === refDay) {
    return 'today';
  }

  // Yesterday: the calendar day before reference
  const yesterday = new Date(refYear, refMonth, refDay - 1);
  if (
    dateYear === yesterday.getFullYear() &&
    dateMonth === yesterday.getMonth() &&
    dateDay === yesterday.getDate()
  ) {
    return 'yesterday';
  }

  // This week: same ISO week number AND same ISO week year
  const dateWeek = getISOWeekNumber(date);
  const refWeek = getISOWeekNumber(reference);
  const dateWeekYear = getISOWeekYear(date);
  const refWeekYear = getISOWeekYear(reference);

  if (dateWeek === refWeek && dateWeekYear === refWeekYear) {
    return 'thisWeek';
  }

  // This year: same calendar year
  if (dateYear === refYear) {
    return 'thisYear';
  }

  return 'older';
}
